import { useState, FormEvent, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const navigate = useNavigate();
  const { activeTheme } = useTheme();

  // Cores do Tema Corporativo IBX
  const primary = activeTheme.colors.primary;
  const secondary = activeTheme.colors.secondary;
  const accent = activeTheme.colors.accent;
  
  // CTA sempre usa laranja (#F28C28) no tema corporativo
  const isCorporativo = activeTheme.id === 'corporativo';
  const ctaColor = accent;

  // Easter egg para acesso admin
  const clickCountRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSecretClick = () => {
    clickCountRef.current += 1;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => { clickCountRef.current = 0; }, 3000);
    if (clickCountRef.current === 5) {
      clickCountRef.current = 0;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      navigate('/admin');
    }
  };

  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  const handleNewsletter = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch('https://readdy.ai/api/form/d6oot1l39lnhn4hh1eig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setStatus('success'); setEmail(''); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <footer
      className="text-white relative overflow-hidden"
      style={{ 
        background: isCorporativo
          ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 50%, ${primary}dd 100%)`
          : `linear-gradient(135deg, ${primary} 0%, ${secondary} 60%, ${primary}dd 100%)` 
      }}
    >
      {/* Overlay sutil para profundidade no tema corporativo */}
      {isCorporativo && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 pointer-events-none" />
      )}

      <div className="container mx-auto px-4 sm:px-6 py-14 lg:py-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">

          {/* Brand + Newsletter */}
          <div className="lg:col-span-5">
            <img
              src="https://public.readdy.ai/ai/img_res/3d1ccac9-fb26-454a-9312-1791e242769c.png"
              alt="IX BRASIL"
              loading="lazy"
              className="h-10 w-auto mb-5"
            />
            <p className="text-base font-semibold mb-3 text-white">Transparência e economia em seguros</p>
            <p className="text-sm leading-relaxed mb-7 max-w-md" style={{ color: 'rgba(255,255,255,0.80)' }}>
              A IX BRASIL revoluciona o mercado de seguros no Brasil, oferecendo apenas{' '}
              <strong className="text-white">10% de taxa de corretagem</strong> com assessoria técnica especializada.
            </p>

            <form data-readdy-form onSubmit={handleNewsletter} className="space-y-3">
              <p className="text-sm font-semibold text-white">Receba dicas de economia em seguros</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Seu melhor e-mail"
                  required
                  className="flex-1 min-w-0 px-4 py-3 rounded-lg text-white text-sm placeholder-white/50 focus:outline-none transition-all duration-200"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.10)', 
                    border: '1px solid rgba(255,255,255,0.20)',
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.40)'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.20)'}
                />
                <button
                  type="submit"
                  className="px-5 py-3 text-white text-sm font-semibold rounded-lg transition-all duration-300 hover:opacity-90 hover:scale-105 whitespace-nowrap cursor-pointer flex-shrink-0"
                  style={{ 
                    backgroundColor: ctaColor, 
                    boxShadow: `0 4px 14px ${ctaColor}55` 
                  }}
                >
                  Inscrever
                </button>
              </div>
              {status === 'success' && (
                <p className="text-xs font-medium flex items-center gap-1 text-white/90">
                  <i className="ri-check-line"></i> Inscrito com sucesso!
                </p>
              )}
              {status === 'error' && (
                <p className="text-xs font-medium flex items-center gap-1" style={{ color: ctaColor }}>
                  <i className="ri-error-warning-line"></i> Erro ao inscrever. Tente novamente.
                </p>
              )}
            </form>
          </div>

          {/* Links */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h3 className="text-sm font-bold mb-4 uppercase tracking-wider text-white/90">Seguros</h3>
              <ul className="space-y-2.5">
                {[
                  { to: '/seguros/seguro-auto', label: 'Seguro Auto' },
                  { to: '/seguros/seguro-vida', label: 'Seguro Vida' },
                  { to: '/seguros/seguro-empresarial', label: 'Seguro Empresarial' },
                  { to: '/seguros/seguro-residencial', label: 'Seguro Residencial' },
                  { to: '/seguros/seguro-patrimonial', label: 'Seguro Patrimonial' },
                  { to: '/seguros/seguro-incendio', label: 'Seguro Incêndio' },
                ].map((item) => (
                  <li key={item.to}>
                    <Link 
                      to={item.to} 
                      className="text-sm transition-colors cursor-pointer inline-block"
                      style={{ color: 'rgba(255,255,255,0.75)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = isCorporativo ? ctaColor : '#FFFFFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                      }}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold mb-4 uppercase tracking-wider text-white/90">Empresa</h3>
              <ul className="space-y-2.5">
                {[
                  { to: '/quem-somos', label: 'Quem Somos' },
                  { to: '/servicos', label: 'Serviços' },
                  { to: '/taxa-corretagem', label: 'Taxa de Corretagem 10%' },
                  { to: '/blog', label: 'Blog' },
                  { to: '/faq', label: 'Perguntas Frequentes' },
                  { to: '/comunidade', label: 'Comunidade' },
                  { to: '/contato', label: 'Contato' },
                ].map((item) => (
                  <li key={item.to}>
                    <Link 
                      to={item.to} 
                      className="text-sm transition-colors cursor-pointer inline-block"
                      style={{ color: 'rgba(255,255,255,0.75)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = isCorporativo ? ctaColor : '#FFFFFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                      }}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-sm font-bold mb-4 uppercase tracking-wider text-white/90">Contato</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-base text-white/80"></i>
                  </div>
                  <a 
                    href="tel:+551140634000" 
                    className="text-sm transition-colors cursor-pointer"
                    style={{ color: 'rgba(255,255,255,0.75)' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
                  >
                    (11) 4063-4000
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-base text-white/80"></i>
                  </div>
                  <a 
                    href="mailto:contato@ixbrasil.com.br" 
                    className="text-sm transition-colors cursor-pointer break-all"
                    style={{ color: 'rgba(255,255,255,0.75)' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
                  >
                    contato@ixbrasil.com.br
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-base" style={{ color: ctaColor }}></i>
                  </div>
                  <a 
                    href="https://wa.me/5511406340000" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm transition-colors cursor-pointer"
                    style={{ color: 'rgba(255,255,255,0.75)' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
                  >
                    (11) 4063-4000
                  </a>
                </li>
              </ul>

              <div className="flex gap-3 mt-6">
                {[
                  { href: '#', icon: 'ri-instagram-line', label: 'Instagram' },
                  { href: '#', icon: 'ri-facebook-fill', label: 'Facebook' },
                  { href: '#', icon: 'ri-linkedin-fill', label: 'LinkedIn' },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer"
                    style={{ backgroundColor: 'rgba(255,255,255,0.10)' }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = isCorporativo 
                        ? `${ctaColor}99` 
                        : 'rgba(255,255,255,0.20)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <i className={`${s.icon} text-lg text-white`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t mt-12 pt-7" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs" style={{ color: 'rgba(255,255,255,0.70)' }}>
            <p>&copy; {currentYear} IX BRASIL. Todos os direitos reservados. CNPJ: 00.000.000/0001-00</p>
            <div className="flex gap-5">
              <Link 
                to="/politica-privacidade" 
                className="transition-colors cursor-pointer"
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.70)'}
              >
                Política de Privacidade
              </Link>
              <a 
                href="#" 
                className="transition-colors cursor-pointer"
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.70)'}
              >
                Termos de Uso
              </a>
            </div>
          </div>
          <div
            onClick={handleSecretClick}
            className="w-full h-8 mt-2 cursor-default"
            aria-hidden="true"
          />
        </div>
      </div>
    </footer>
  );
};

export default Footer;